/**
 * SECP / SECN — 11 sectors × drifts, base vol, factor loadings (plan §0.2).
 * Ported verbatim from demos/Meridian Terminal.dc.html.
 */
import type { SectorId } from '@meridian/contracts';

export interface SectorParams {
  /** Per-window drift means aligned with WIN order. */
  readonly d: readonly [number, number, number, number];
  /** Base annualised volatility. */
  readonly v: number;
  /** Five factor loadings. */
  readonly f: readonly [number, number, number, number, number];
}

/* Object.freeze is shallow, so each row's `d` and `f` tuples are frozen too.
   The literal rows below stay byte-identical to the demo for line-by-line audit. */
function freezeSectorParams(
  table: Record<SectorId, SectorParams>,
): Readonly<Record<SectorId, SectorParams>> {
  Object.values(table).forEach((p) => {
    Object.freeze(p.d);
    Object.freeze(p.f);
    Object.freeze(p);
  });
  return Object.freeze(table);
}

export const SECN: Readonly<Record<SectorId, string>> = Object.freeze({
  TEC: 'Technology',
  COM: 'Communication Svcs',
  DIS: 'Consumer Discretionary',
  STA: 'Consumer Staples',
  FIN: 'Financials',
  HLT: 'Health Care',
  IND: 'Industrials',
  ENE: 'Energy',
  UTL: 'Utilities',
  REI: 'Real Estate',
  MAT: 'Materials',
});

export const SECP: Readonly<Record<SectorId, SectorParams>> = freezeSectorParams({
  TEC: { d: [0.031, 0.072, 0.243, 0.612], v: 0.29, f: [0.45, 1.0, -0.55, -0.1, -0.55] },
  COM: { d: [0.022, 0.058, 0.191, 0.488], v: 0.27, f: [0.45, 0.8, -0.35, -0.1, -0.4] },
  DIS: { d: [0.014, 0.034, 0.126, 0.303], v: 0.26, f: [0.5, 0.55, -0.55, -0.25, -0.55] },
  STA: { d: [0.006, 0.012, 0.048, 0.121], v: 0.14, f: [0.35, -0.6, 0.15, -0.15, 1.0] },
  FIN: { d: [0.019, 0.048, 0.167, 0.402], v: 0.21, f: [0.5, -0.15, 1.0, 0.05, -0.35] },
  HLT: { d: [-0.004, 0.009, 0.036, 0.088], v: 0.18, f: [0.35, -0.2, -0.1, -0.1, 0.85] },
  IND: { d: [0.012, 0.031, 0.114, 0.276], v: 0.19, f: [0.5, 0.3, 0.35, 0.4, -0.45] },
  ENE: { d: [-0.017, -0.028, -0.041, 0.152], v: 0.28, f: [0.4, -0.45, 0.35, 1.0, -0.3] },
  UTL: { d: [0.009, 0.026, 0.092, 0.164], v: 0.16, f: [0.3, -0.65, -0.75, 0.25, 0.85] },
  REI: { d: [-0.008, 0.004, 0.021, -0.032], v: 0.22, f: [0.4, -0.25, -1.0, -0.05, 0.4] },
  MAT: { d: [0.003, 0.017, 0.055, 0.108], v: 0.21, f: [0.45, 0.0, 0.3, 0.75, -0.3] },
});
